import { useCallback, useEffect } from 'react';
import { z } from 'zod';
import { VIEW_QUERY_PARAM } from '@/features/planner/hooks/useActiveView';
import { EVENT_FILTER_PARAM_KEYS } from '@/features/planner/hooks/useExperienceEventFilters';
import { useQueryParams } from '@/hooks/useQueryParams';
import {
  ExperienceChildConfigKind,
  ExperienceChildConfigName,
  ExperienceLayouts,
  FeedOriginKind,
  InteractionAppNames,
} from '@/types/gql.generated';
import {
  CUSTOMIZE_HOME_EDIT_VALUE,
  isCustomizeControlId,
  isMagicWorkflow,
  MAGIC_EDIT_VALUE,
  type EditValue,
  type MagicWorkflow,
} from './customizeOptions';

export const ACTION_QUERY_PARAM = 'action';
export const EDIT_QUERY_PARAM = 'edit';
export const SOURCE_QUERY_PARAM = 'source';
export const TAB_QUERY_PARAM = 'tab';
export const LAYOUT_QUERY_PARAM = 'layout';
// Shared by `source=add` and `view=add`.
export const ADD_VALUE = 'add';
// The Change-layout sub-mode of the `layout` param: `layout=change` (open) or
// `layout=change:<ExperienceLayouts>` (a layout selected). Create mode instead
// stores the picked config plus its resolved layout+kind as a colon-joined
// triple (`layout=<config>:<layoutId>:<kind>`).
const CHANGE_VALUE = 'change';

// Sub-selection params scoped to whichever panel `edit` names (a chosen source,
// a settings tab, the add-view/change-layout selection). Changing `edit` resets
// them, so they're defined once here and cleared as a set everywhere the active
// panel changes — add a new panel-scoped param here and every reset below picks it up.
const CLEARED_PANEL_SUB_PARAMS: Record<string, null> = {
  [SOURCE_QUERY_PARAM]: null,
  [TAB_QUERY_PARAM]: null,
  [LAYOUT_QUERY_PARAM]: null,
};
const PANEL_SUB_PARAM_KEYS = Object.keys(CLEARED_PANEL_SUB_PARAMS);

// The platform bar hides the event-filter controls while the add-view /
// change-layout flow is open, so entering the flow clears any applied filters —
// otherwise they'd keep filtering the preview with no visible affordance.
const CLEARED_EVENT_FILTER_PARAMS: Record<string, null> = Object.fromEntries(
  EVENT_FILTER_PARAM_KEYS.map((key) => [key, null])
);

// ── Validation ──────────────────────────────────────────────────────────────────
// Validate raw params against the supported values. Validation never throws: an
// unsupported value falls back to null, leaving the other params intact.

const editSchema = z
  .string()
  .nullable()
  .catch(null)
  .transform((value): EditValue | null =>
    isCustomizeControlId(value)
      ? value
      : value === MAGIC_EDIT_VALUE || value === CUSTOMIZE_HOME_EDIT_VALUE
        ? value
        : null
  );

const actionSchema = z.enum(InteractionAppNames).nullable().catch(null);

// The source kind being added drives the `source=add:<kind>` param, using the
// canonical FeedOriginKind values (GOOGLE / ICS / EXPERIENCE).
const addChannelSchema = z.enum(FeedOriginKind);

/** Parsed `source` param: the add flow (optionally a kind) or a selected feed id. */
export type ParsedSource =
  | { mode: 'add'; channel: FeedOriginKind | null }
  | { mode: 'feed'; feedId: string }
  | null;

const parseSource = (raw: string | null): ParsedSource => {
  if (!raw) return null;
  if (raw === ADD_VALUE) return { mode: 'add', channel: null };
  if (raw.startsWith(`${ADD_VALUE}:`)) {
    const result = addChannelSchema.safeParse(raw.slice(ADD_VALUE.length + 1));
    return { mode: 'add', channel: result.success ? result.data : null };
  }
  // Feed-id existence is validated by the consumer against experience.feeds.
  return { mode: 'feed', feedId: raw };
};

/**
 * The customize params that deep-link into the Add-source flow — optionally straight
 * into one channel's connect sub-view. The imperative counterpart of
 * `openAddSource`/`openAddChannel`, for callers that can't navigate themselves: a
 * round trip that leaves the app (Stripe checkout) has to describe where to come
 * back to as params, since it rebuilds the URL from the pathname alone and would
 * otherwise drop the panel the user was in.
 */
export const getAddSourceParams = (
  channel?: FeedOriginKind | null
): Record<string, string> => ({
  [EDIT_QUERY_PARAM]: 'sources',
  [SOURCE_QUERY_PARAM]: channel ? `${ADD_VALUE}:${channel}` : ADD_VALUE,
});

const configSchema = z.enum(ExperienceChildConfigName);
const layoutIdSchema = z.enum(ExperienceLayouts);
// The URL kind segment is the config kind a customize layout renders as
// (Interactive / Printable).
const kindSchema = z.enum([
  ExperienceChildConfigKind.Interactive,
  ExperienceChildConfigKind.Printable,
]);

/**
 * Parsed `layout` param — the Add-a-view / Change-layout selection. Disambiguated
 * by `view=add` (create) vs otherwise (change), mirroring how `source` is scoped
 * by the active panel:
 * - create: `layout=<config>:<layoutId>:<kind>` → `{ config, layoutId, kind }`
 *   (null config = nothing picked yet; create mode stays open on `view=add`
 *   regardless). `config` is the creation key; `layoutId`/`kind` are the server's
 *   display answer captured at selection time so the live preview renders straight
 *   from the URL, with no client-side config→layout/kind map to keep in sync.
 * - change: `layout=change` → `{ layoutId: null }`, `layout=change:<id>` → the layout.
 */
export type ParsedAddViewLayout =
  | {
      mode: 'create';
      config: ExperienceChildConfigName | null;
      layoutId: ExperienceLayouts | null;
      kind: ExperienceChildConfigKind | null;
    }
  | { mode: 'change'; layoutId: ExperienceLayouts | null }
  | null;

const parseLayout = (
  raw: string | null,
  isAddingView: boolean
): ParsedAddViewLayout => {
  if (isAddingView) {
    if (!raw)
      return { mode: 'create', config: null, layoutId: null, kind: null };
    // Availability of each part is validated independently; a missing/stale segment
    // falls back to null rather than invalidating the whole selection. The consumer
    // additionally checks `config` against the fetched configs before creating.
    const [configRaw, layoutIdRaw, kindRaw] = raw.split(':');
    const config = configSchema.safeParse(configRaw);
    const layoutId = layoutIdSchema.safeParse(layoutIdRaw);
    const kind = kindSchema.safeParse(kindRaw);
    return {
      mode: 'create',
      config: config.success ? config.data : null,
      layoutId: layoutId.success ? layoutId.data : null,
      kind: kind.success ? kind.data : null,
    };
  }
  if (raw === CHANGE_VALUE) return { mode: 'change', layoutId: null };
  if (raw?.startsWith(`${CHANGE_VALUE}:`)) {
    const result = layoutIdSchema.safeParse(raw.slice(CHANGE_VALUE.length + 1));
    return { mode: 'change', layoutId: result.success ? result.data : null };
  }
  return null;
};

// ── Hook ────────────────────────────────────────────────────────────────────────
// Single source of truth for reading, validating, and setting the Customize-mode
// URL params (edit / source / view). All writes use replace — the experience being
// edited is the resource, so panel navigation shouldn't pollute browser history.

export const useCustomizeParams = () => {
  const { queryParams, setQueryParams, removeQueryParam } = useQueryParams();

  const action = actionSchema.parse(queryParams.get(ACTION_QUERY_PARAM));
  const edit = editSchema.parse(queryParams.get(EDIT_QUERY_PARAM));
  const source = parseSource(queryParams.get(SOURCE_QUERY_PARAM));
  // Raw section id for the active full-screen panel tab (currently Schedule
  // settings). The consumer validates it against its own section list, so no
  // parsing here — same contract as a `source` feed id.
  const tab = queryParams.get(TAB_QUERY_PARAM);
  const isAddingView = queryParams.get(VIEW_QUERY_PARAM) === ADD_VALUE;
  const layout = parseLayout(queryParams.get(LAYOUT_QUERY_PARAM), isAddingView);

  const setEdit = useCallback(
    (value: EditValue | null) =>
      value === null
        ? removeQueryParam([EDIT_QUERY_PARAM, ...PANEL_SUB_PARAM_KEYS], {
            replace: true,
          })
        : setQueryParams(
            {
              [EDIT_QUERY_PARAM]: value,
              [ACTION_QUERY_PARAM]: null,
              ...CLEARED_PANEL_SUB_PARAMS,
            },
            { replace: true }
          ),
    [removeQueryParam, setQueryParams]
  );

  const setAction = useCallback(
    (value: InteractionAppNames | null) => {
      if (value) {
        setQueryParams(
          {
            [ACTION_QUERY_PARAM]: value,
            [EDIT_QUERY_PARAM]: null,
            ...CLEARED_PANEL_SUB_PARAMS,
          },
          { replace: true }
        );
      } else {
        removeQueryParam([ACTION_QUERY_PARAM], {
          replace: true,
        });
      }
    },
    [removeQueryParam, setQueryParams]
  );

  // Exit customize: drop edit + source + tab or an active action, plus a lingering
  // view=add — but keep a real view={childId} so the user stays on the view they
  // were customizing. One write so the keys don't clobber each other.
  // The customize home screen. Like exitCustomize it leaves whatever was open:
  // a panel's sub-selection, an interaction, the add-view flow.
  const openCustomizeHome = useCallback(
    () =>
      setQueryParams(
        {
          [EDIT_QUERY_PARAM]: CUSTOMIZE_HOME_EDIT_VALUE,
          [ACTION_QUERY_PARAM]: null,
          ...CLEARED_PANEL_SUB_PARAMS,
          ...(queryParams.get(VIEW_QUERY_PARAM) === ADD_VALUE && {
            [VIEW_QUERY_PARAM]: null,
          }),
        },
        { replace: true }
      ),
    [queryParams, setQueryParams]
  );

  const exitCustomize = useCallback(() => {
    const keys = [EDIT_QUERY_PARAM, ...PANEL_SUB_PARAM_KEYS];
    if (action) {
      keys.push(ACTION_QUERY_PARAM);
    }
    if (queryParams.get(VIEW_QUERY_PARAM) === ADD_VALUE) {
      keys.push(VIEW_QUERY_PARAM);
    }
    removeQueryParam(keys, { replace: true });
  }, [action, queryParams, removeQueryParam]);

  const setSource = useCallback(
    (value: string | null) =>
      setQueryParams({ [SOURCE_QUERY_PARAM]: value }, { replace: true }),
    [setQueryParams]
  );
  const openAddSource = useCallback(() => setSource(ADD_VALUE), [setSource]);
  const openAddChannel = useCallback(
    (channel: FeedOriginKind) => setSource(`${ADD_VALUE}:${channel}`),
    [setSource]
  );
  const selectFeed = useCallback(
    (feedId: string) => setSource(feedId),
    [setSource]
  );
  const clearSource = useCallback(() => setSource(null), [setSource]);

  // Sets the active tab for the current full-screen panel. Leaves `edit`
  // untouched, mirroring how `setSource` updates the sidebar independently.
  const setTab = useCallback(
    (value: string | null) =>
      setQueryParams({ [TAB_QUERY_PARAM]: value }, { replace: true }),
    [setQueryParams]
  );

  // Atomically open a panel on a specific tab — one write, because setEdit clears
  // the panel sub-params, so a setEdit + setTab pair would drop the tab (and each
  // call reads a stale queryParams snapshot besides).
  const openEditTab = useCallback(
    (value: EditValue, tabValue: string) =>
      setQueryParams(
        {
          [EDIT_QUERY_PARAM]: value,
          [ACTION_QUERY_PARAM]: null,
          ...CLEARED_PANEL_SUB_PARAMS,
          [TAB_QUERY_PARAM]: tabValue,
        },
        { replace: true }
      ),
    [setQueryParams]
  );

  // Enter the Magic panel in a specific workflow.
  const openMagicWorkflow = useCallback(
    (workflow: MagicWorkflow) => openEditTab(MAGIC_EDIT_VALUE, workflow),
    [openEditTab]
  );

  const openAddView = useCallback(
    () =>
      setQueryParams(
        {
          [VIEW_QUERY_PARAM]: ADD_VALUE,
          [LAYOUT_QUERY_PARAM]: null,
          ...CLEARED_EVENT_FILTER_PARAMS,
        },
        { replace: true }
      ),
    [setQueryParams]
  );

  // ── Add-a-view / Change-layout selection (`layout` param) ──────────────────
  // Mirrors setSource: a single replace write, with intent-named wrappers. The
  // selection is a sub-state of the Views panel, disambiguated by `view=add`.
  const setLayout = useCallback(
    (value: string | null) =>
      setQueryParams({ [LAYOUT_QUERY_PARAM]: value }, { replace: true }),
    [setQueryParams]
  );
  // Store the config (the creation key) together with the layout + kind the
  // server resolves it to, so the live preview reads all three from the URL.
  const selectConfig = useCallback(
    (
      config: ExperienceChildConfigName,
      layoutId: ExperienceLayouts,
      kind: ExperienceChildConfigKind
    ) => setLayout(`${config}:${layoutId}:${kind}`),
    [setLayout]
  );
  const selectChangeLayout = useCallback(
    (layoutId: ExperienceLayouts) => setLayout(`${CHANGE_VALUE}:${layoutId}`),
    [setLayout]
  );
  const openChangeLayout = useCallback(
    () =>
      setQueryParams(
        { [LAYOUT_QUERY_PARAM]: CHANGE_VALUE, ...CLEARED_EVENT_FILTER_PARAMS },
        { replace: true }
      ),
    [setQueryParams]
  );
  // Deselect the current layout while keeping the picker open (removes `layout`
  // but leaves `view=add` in create mode).
  const clearLayout = useCallback(() => setLayout(null), [setLayout]);

  // Close the whole add-view flow: drop `layout` and, in create mode, the
  // `view=add` signal too — one write so the keys don't clobber each other.
  const closeAddView = useCallback(() => {
    const keys = [LAYOUT_QUERY_PARAM];
    if (queryParams.get(VIEW_QUERY_PARAM) === ADD_VALUE) {
      keys.push(VIEW_QUERY_PARAM);
    }
    removeQueryParam(keys, { replace: true });
  }, [queryParams, removeQueryParam]);

  // Finish create: rewrite view=add → view=childId and clear layout in a single
  // replace, both closing the flow and switching the page to the new child.
  const finishCreate = useCallback(
    (childId: string) =>
      setQueryParams(
        { [VIEW_QUERY_PARAM]: childId, [LAYOUT_QUERY_PARAM]: null },
        { replace: true }
      ),
    [setQueryParams]
  );

  // Atomically enter the Views panel and open the add-view flow in one write so
  // neither call clobbers the other (each call reads a stale queryParams snapshot).
  // Sources in one write, landing on the Add-source view. Opening the panel and
  // then setting `source` cannot work: setEdit clears the panel sub-params, and
  // a plain `edit=sources` falls back to the first rail section, which on a
  // planner with no feeds is the "No sources" placeholder.
  const openSourcesWithAdd = useCallback(
    () =>
      setQueryParams(
        {
          [EDIT_QUERY_PARAM]: 'sources',
          [ACTION_QUERY_PARAM]: null,
          ...CLEARED_PANEL_SUB_PARAMS,
          [SOURCE_QUERY_PARAM]: ADD_VALUE,
        },
        { replace: true }
      ),
    [setQueryParams]
  );

  const openViewsWithAddView = useCallback(
    () =>
      setQueryParams(
        {
          [EDIT_QUERY_PARAM]: 'views',
          [ACTION_QUERY_PARAM]: null,
          ...CLEARED_PANEL_SUB_PARAMS,
          [VIEW_QUERY_PARAM]: ADD_VALUE,
          ...CLEARED_EVENT_FILTER_PARAMS,
        },
        { replace: true }
      ),
    [setQueryParams]
  );

  return {
    edit,
    source,
    tab,
    isAddingView,
    layout,
    action,
    setAction,
    setEdit,
    openEditTab,
    openMagicWorkflow,
    exitCustomize,
    openCustomizeHome,
    openAddSource,
    openAddChannel,
    selectFeed,
    clearSource,
    setTab,
    openAddView,
    openViewsWithAddView,
    openSourcesWithAdd,
    setLayout,
    selectConfig,
    selectChangeLayout,
    openChangeLayout,
    clearLayout,
    closeAddView,
    finishCreate,
  };
};

// ── Normalization ─────────────────────────────────────────────────────────────
// Strip stale/orphan customize params so the URL only holds representable UI state.
// Removes just the offending param (falling back to its default) and keeps the rest.
// Self-contained — mount once (in EditPageExperience), never per-consumer.

// The full-screen panels whose sub-navigation rides the `tab` param. The Magic
// panel also rides `tab` (its workflow), but with a static value set — it's
// validated by its own normalization branch below.
const TABBED_EDIT_VALUES: EditValue[] = ['schedule', 'messages'];

type NormalizeOptions = {
  /**
   * Whether customize mode can render at all — Planner-only and desktop-only,
   * since EditPageExperience mounts neither the bar nor the sidebar below `md`.
   *
   * Defaults to `true`, unlike the gates below: those guard optional panels,
   * where "unknown" safely means unavailable, but defaulting the customize tabs
   * off would make an omitted option strip a valid panel.
   */
  customizeEnabled?: boolean;
  /** Whether `edit=magic` (the add-content panel) is currently enabled. */
  magicEnabled?: boolean;
  /** Whether `edit=messages` (the Text messages panel) is currently enabled. */
  messagesEnabled?: boolean;
  /**
   * Whether anything behind the enabled gates above is still resolving — the
   * ConfigCat flags, or a profile query a gate reads. Normalization defers while
   * true: those gates read as their defaults (false) until their inputs land, and
   * stripping on the transient value would eat a deep-linked/refreshed
   * `edit=magic` (+ its `tab`) or `edit=messages`.
   */
  flagsLoading?: boolean;
};

export const useNormalizeCustomizeParams = ({
  customizeEnabled = true,
  magicEnabled = false,
  messagesEnabled = false,
  flagsLoading = false,
}: NormalizeOptions = {}) => {
  const { queryParams, removeQueryParam } = useQueryParams();

  useEffect(() => {
    if (flagsLoading) return;
    const editRaw = queryParams.get(EDIT_QUERY_PARAM);
    const edit = editSchema.parse(editRaw);
    const source = queryParams.get(SOURCE_QUERY_PARAM);
    const tabRaw = queryParams.get(TAB_QUERY_PARAM);
    const view = queryParams.get(VIEW_QUERY_PARAM);
    const layoutRaw = queryParams.get(LAYOUT_QUERY_PARAM);

    // The add-view / change-layout flow hides the event-filter controls, so any
    // filter param while the flow is active is non-representable state — strip
    // it. The imperative entry points (openViewsWithAddView etc.) already clear
    // filters atomically; this covers the paths they can't, e.g. a deep-linked
    // or refreshed `?edit=views&view=add&categories=…`. Mirrors addViewMode.
    const layoutIsChange =
      layoutRaw === CHANGE_VALUE ||
      (layoutRaw?.startsWith(`${CHANGE_VALUE}:`) ?? false);
    const isAddViewFlowActive =
      edit === 'views' && (view === ADD_VALUE || layoutIsChange);
    const hasFilterParams = EVENT_FILTER_PARAM_KEYS.some(
      (key) => queryParams.get(key) !== null
    );

    // Runs first (highest priority) so it strips a disjoint param set in its own
    // pass — the cascade below removes at most one param per pass to avoid the
    // shared-snapshot clobber in useQueryParams; any structural fix settles on a
    // later pass once filters are gone.
    if (isAddViewFlowActive && hasFilterParams) {
      removeQueryParam(EVENT_FILTER_PARAM_KEYS, { replace: true });
    } else if (editRaw !== null && edit === null) {
      removeQueryParam([EDIT_QUERY_PARAM, SOURCE_QUERY_PARAM], {
        replace: true,
      });
    } else if (
      (isCustomizeControlId(edit) || edit === CUSTOMIZE_HOME_EDIT_VALUE) &&
      !customizeEnabled
    ) {
      // Below `md` nothing renders the panel, yet the mode still reads as
      // active — and the add-view sub-flow then hides the view picker, filter,
      // Key, and the mobile Magic bar, leaving nothing to cancel with. The
      // `view=add` branch below clears the rest of the flow on the next pass.
      removeQueryParam([EDIT_QUERY_PARAM, ...PANEL_SUB_PARAM_KEYS], {
        replace: true,
      });
    } else if (edit === MAGIC_EDIT_VALUE && !magicEnabled) {
      // `edit=magic` is only representable when the add-content panel is
      // enabled; otherwise strip it (and its workflow `tab`) so a stale value
      // doesn't hide the Magic button while nothing renders the panel.
      removeQueryParam([EDIT_QUERY_PARAM, ...PANEL_SUB_PARAM_KEYS], {
        replace: true,
      });
    } else if (edit === 'messages' && !messagesEnabled) {
      // Text messages is a gated tab (family planners behind a flag) — strip a
      // deep-linked edit=messages when the gate is off so customize mode isn't
      // stuck on a panel that never renders.
      removeQueryParam([EDIT_QUERY_PARAM, ...PANEL_SUB_PARAM_KEYS], {
        replace: true,
      });
    } else if (source !== null && edit !== 'sources') {
      removeQueryParam([SOURCE_QUERY_PARAM], { replace: true });
    } else if (
      edit === MAGIC_EDIT_VALUE &&
      tabRaw !== null &&
      !isMagicWorkflow(tabRaw)
    ) {
      // Magic's `tab` holds a workflow id; an unknown value is
      // non-representable — strip it back to the calendar chat.
      removeQueryParam([TAB_QUERY_PARAM], { replace: true });
    } else if (
      tabRaw !== null &&
      edit !== MAGIC_EDIT_VALUE &&
      (edit === null || !TABBED_EDIT_VALUES.includes(edit))
    ) {
      removeQueryParam([TAB_QUERY_PARAM], { replace: true });
    } else if (view === ADD_VALUE && edit !== 'views') {
      removeQueryParam([VIEW_QUERY_PARAM], { replace: true });
    } else if (layoutRaw !== null && edit !== 'views') {
      // `layout` is a Views-panel sub-selection; strip it outside that panel.
      removeQueryParam([LAYOUT_QUERY_PARAM], { replace: true });
    } else if (layoutRaw !== null) {
      // Within Views: `layout` must agree with the mode. A change value only
      // makes sense outside create, and a create-config value only makes sense
      // with view=add — strip a value that contradicts the current mode.
      const isChange =
        layoutRaw === CHANGE_VALUE || layoutRaw.startsWith(`${CHANGE_VALUE}:`);
      const viewIsAdd = view === ADD_VALUE;
      if ((isChange && viewIsAdd) || (!isChange && !viewIsAdd)) {
        removeQueryParam([LAYOUT_QUERY_PARAM], { replace: true });
      }
    }
  }, [
    queryParams,
    removeQueryParam,
    customizeEnabled,
    magicEnabled,
    messagesEnabled,
    flagsLoading,
  ]);
};
